import { Component, computed, effect, inject, signal, input } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { startWith, switchMap } from 'rxjs';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';

import { ApContentService, ApContentPipe } from '@ap/content';
import { ApDateService } from '@ap/date';
import { ErrorMessages } from '@ap/form/errors';
import { ApModalService, ModalClosedReason, ModalClosedResult, ModalOptions } from '@ap/modal';
import {
    ApSelectorModalComponent,
    ApSelectorModalOptions,
    ApSelectorOption,
    ApSelectorSelectionType,
    ApSelectorSelectComponent
} from '@ap/selector';

import content from './person-selector.content.json';
import { Person } from './person-selector.model';

@Component({
    selector: 'ap-person-selector',
    templateUrl: './person-selector.component.html',
    imports: [ApSelectorSelectComponent, ReactiveFormsModule, ApContentPipe]
})
export class ApPersonSelectorComponent {
    private readonly contentService = inject(ApContentService);
    private readonly modalService = inject(ApModalService);

    /**
     * Required - List of people to select from.
     */
    public readonly options = input.required<Person[]>();

    /**
     * Optional control input. Passed on to radio buttons
     */
    public readonly control = input<FormControl<Person>>(new FormControl());

    constructor() {
        this.contentService.addContentItems(content);

        effect(() => {
            this.setSelectedOptions(this.selectorOptions());
        });
        effect(() => {
            this.setPreselectedValue(this.controlValue());
        });
    }

    protected readonly selectorOptions = computed<ApSelectorOption[]>(() => {
        const options = this.options();
        return options?.map((option) => this.mapSelectorOption(option)) || [];
    });
    protected messages: ErrorMessages = {
        required: 'personSelector.error.required'
    };
    protected selectedOptions = signal<ApSelectorOption[]>(undefined);

    /**
     * Replacement for valuechanges subscription
     * Allows you to watch the value of the control as a signal
     */
    private readonly controlValue = toSignal(
        toObservable(this.control).pipe(switchMap((ctrl) => ctrl.valueChanges.pipe(startWith(this.control().value))))
    );

    /**
     * Type of selection allowed for user.
     * - `single` - user can select one account
     * - `multiple` - user can select one, multiple or all accounts
     */
    protected selectionType: ApSelectorSelectionType = 'single';

    /**
     * Watch value changes to update selected option
     */
    private setPreselectedValue(controlValue: Person): void {
        if (controlValue) {
            this.setSelectedOptionsFromValue(controlValue);
        }
    }

    /**
     * If only one option, select that option
     */
    private setSelectedOptions(selectorOptions: ApSelectorOption[]): void {
        if (selectorOptions && selectorOptions.length === 1) {
            this.selectedOptions.set([selectorOptions[0]]);
            this.control()?.setValue(this.selectedOptions()[0].data);
        }
    }

    /**
     * Open selector modal with correct options
     */
    protected openModal(): void {
        const modalOptions = new ModalOptions<ApSelectorModalOptions>({
            options: this.selectorOptions(),
            selectedOptions: this.selectedOptions(),
            selectionType: this.selectionType
        });
        modalOptions.title = this.contentService.get('personSelector.label');

        const modalContainer = this.modalService.open(ApSelectorModalComponent, modalOptions);
        modalContainer.afterClosed().subscribe((result: ModalClosedResult<ApSelectorOption[]>) => {
            const control = this.control();
            if (control) {
                control.markAsDirty();
            }

            if (result.reason === ModalClosedReason.SELECTED) {
                this.selectedOptions.set(result.data);
                control?.setValue(this.selectedOptions()[0].data);
            }
        });
    }

    /**
     * Set selected options from passed value
     */
    private setSelectedOptionsFromValue(controlValue: Person): void {
        const selectorOptions = this.selectorOptions();
        if (!selectorOptions || !controlValue) {
            return;
        }
        const selectedId = this.generateOptionId(controlValue);
        this.selectedOptions.set(selectorOptions.filter((option) => selectedId === option.id));
    }

    /**
     * Map plain Person to selector option
     */
    private mapSelectorOption(option: Person): ApSelectorOption {
        return {
            title: option.name,
            text: `${this.contentService.get('personSelector.birthDate')}: ${ApDateService.format(option.birthDate, 'dd LLLL yyyy')}`,
            id: this.generateOptionId(option),
            data: { ...option }
        };
    }

    /**
     * Generate unique id for option
     */
    private generateOptionId(option: Person): string {
        return `${this.contentService.get('personSelector.birthDate')}-${option.name}-${option.birthDate.getTime()}`
            .replace(/\s+/g, '-')
            .toLowerCase();
    }
}
